import { QuickReviewService } from "../../../../api/services/quickReviewService";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import {
  QuickReviewListRequest,
  QuickReviewListResponse,
} from "../../../../data-model/quickReviewModel";
import { QuickReviewOrdersState } from "../hooks/useQuickReviewOrdersTableData";
import {
  QuickReviewTableData,
  QuickReviewTableUtils,
} from "../utils/quickReviewTableUtils";

/**
 * Fetches initial Quick Review board data by executing multiple list payloads
 * sequentially, combining the results, and updating both the board state and
 * table data.
 *
 * @param payloads - Array of list request objects, one per page to fetch
 * @param authStatusOptions - Available auth status options for badge rendering
 * @param setQuickReviewOrdersState - State setter for the board loading/data/error state
 * @param setTableData - State setter for the transformed table row data
 * @param setInitialDataFetchingExecuted - Flags that the initial fetch cycle has completed
 */
export const makePostCallForInitialDataFetch = async (
  payloads: QuickReviewListRequest[],
  authStatusOptions: AuthStatusOptionModel[],
  setQuickReviewOrdersState: React.Dispatch<
    React.SetStateAction<QuickReviewOrdersState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<QuickReviewTableData[]>>,
  setInitialDataFetchingExecuted: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  setQuickReviewOrdersState((prev) => ({ ...prev, loading: true }));

  try {
    const data: QuickReviewListResponse[] = [];

    for (const payload of payloads) {
      const response = await QuickReviewService.list(payload);
      data.push(response);
    }

    const combinedData = appendAllResponses(data);

    setQuickReviewOrdersState((prev) => ({
      ...prev,
      data: combinedData,
      totalCount: combinedData.total_count,
    }));
    setTableData(
      QuickReviewTableUtils.generateTableDataFromQuickReviewItems(
        combinedData.items,
        authStatusOptions,
      ),
    );
  } catch (error) {
    setQuickReviewOrdersState((prev) => ({
      ...prev,
      error: error instanceof Error ? error.message : String(error),
    }));
  } finally {
    setQuickReviewOrdersState((prev) => ({ ...prev, loading: false }));
    setInitialDataFetchingExecuted(true);
  }
};

/**
 * Merges multiple Quick Review response pages into a single response by
 * concatenating their items arrays.
 *
 * @param responses - Array of paginated Quick Review responses to combine
 * @returns A single response with all items merged and metadata from the last response
 */
const appendAllResponses = (responses: QuickReviewListResponse[]) => {
  return responses.reduce(
    (acc, response) => ({
      ...response,
      items: [...(acc.items ?? []), ...(response.items ?? [])],
    }),
    {} as QuickReviewListResponse,
  );
};
